import { isDefined, isEmpty, isNotDefined } from '@sgme/fp';

import type { FlexiForwardRfsMarginDataState } from '@/store/flexi-forward/api/requestFlexiForwardRfs.model.ts';
import type { FlexiForwardQuote, FlexiForwardRfs } from '@/store/flexi-forward/state';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

export interface Currencies {
  currency1: string | undefined;
  currency2: string | undefined;
}

export function getCurrenciesFromCurrencyPair(currencyPair: string | undefined): Currencies {
  if (isNotDefined(currencyPair) || isEmpty(currencyPair)) {
    return { currency1: undefined, currency2: undefined };
  }

  const currencies = currencyPair.split('/');

  const [currency1, currency2] = currencies;

  return { currency1, currency2 };
}

export function getFlexiForwardRfsQuote(rfsState: FlexiForwardRfs | undefined) {
  return isDefined(rfsState) && 'quote' in rfsState && isDefined(rfsState.quote) ? rfsState.quote : undefined;
}

export function getRfsMarginData(rfsState: FlexiForwardRfs | undefined): FlexiForwardRfsMarginDataState | undefined {
  const quote = getFlexiForwardRfsQuote(rfsState);

  return quote?.marginData ?? undefined;
}

export interface FlexiForwardRfsPremiumData {
  premiumAmount: readonly [string, string];
  premiumPercent: readonly [string, string];
}

export function selectFlexiForwardRfsPremiumData(
  quote: FlexiForwardQuote | undefined,
): FlexiForwardRfsPremiumData | undefined {
  if (isNotDefined(quote?.premiumData)) {
    return undefined;
  }

  return {
    premiumAmount: [quote.premiumData.askAmountCcy1, quote.premiumData.askAmountCcy2] as const,
    premiumPercent: [quote.premiumData.askPercentCcy1, quote.premiumData.askPercentCcy2] as const,
  };
}

export function getPremiumData(
  premiumData: FlexiForwardRfsPremiumData | undefined,
  premiumCurrencyIndex: CurrencyIndex,
) {
  if (isNotDefined(premiumData)) {
    return {
      premiumAmount: 0,
      premiumInPercent: 0,
    };
  }

  const { premiumAmount, premiumPercent } = premiumData;

  const premiumAmountString = isDefined(premiumAmount) ? premiumAmount[premiumCurrencyIndex - 1] : undefined;
  const premiumPercentString = isDefined(premiumPercent) ? premiumPercent[premiumCurrencyIndex - 1] : undefined;

  return {
    premiumAmount: isDefined(premiumAmountString) ? Number.parseFloat(premiumAmountString) : 0,
    premiumInPercent: isDefined(premiumPercentString) ? Number.parseFloat(premiumPercentString) * 100 : 0,
  };
}
